// ============================================================================
// AuthService
// -----------
// Auth module related services
// ============================================================================

// -------
// Imports
// -------
import API from '@/apis/AuthApi'

import { store as STORE } from '@/store'
import { i18n } from '@/plugins/i18n'

import AppService from '@/services/AppService'
import KeycloakService from '@/services/Auth/KeycloakService'

const IDENTITY_PROVIDER_MODULE_NAME = 'identity-provider'

const DEFAULT_RETRY_AFTER_SECONDS = 60

const LOGIN_ERROR_META_KEYS = ['code', 'message', 'retry_after_seconds']

const LOGIN_FIELDS = ['email', 'password']

// Statuts traduisant une indisponibilité passagère (un 500 relève plutôt d'un bug serveur)
const SERVICE_UNAVAILABLE_STATUSES = [502, 503, 504]

export const LoginErrorTypes = Object.freeze({
	INVALID_CREDENTIALS: 'invalid_credentials',
	INVALID_INPUT: 'invalid_input',
	THROTTLED: 'throttled',
	SERVICE_UNAVAILABLE: 'service_unavailable',
	UNKNOWN: 'unknown'
})

const Private = {
	isBlank: function (value) {
		return value === undefined || value === null || `${value}`.trim() === ''
	},
	parseHttpDateSeconds: function (value) {
		const date = Date.parse(value)
		return isNaN(date) ? null : Math.max(0, Math.ceil((date - Date.now()) / 1000))
	},
	parseRetryAfterValue: function (value) {
		const seconds = Number(value)
		return Number.isFinite(seconds) ? seconds : Private.parseHttpDateSeconds(value)
	},
	parseRetryAfterHeader: function (value) {
		return Private.isBlank(value) ? null : Private.parseRetryAfterValue(value)
	},
	getRetryAfterSeconds: function (response) {
		const fromBody = Number(response?.data?.error?.retry_after_seconds)
		let result = Number.isFinite(fromBody) && fromBody > 0 ? fromBody : Private.parseRetryAfterHeader(response?.headers?.['retry-after'])
		if (!result || result <= 0) {
			result = DEFAULT_RETRY_AFTER_SECONDS
		}
		return Math.ceil(result)
	},
	toNonEmptyString: function (value) {
		return typeof value === 'string' && value.trim() !== '' ? value : null
	},
	getFirstFieldMessage: function (fieldErrors) {
		const candidate = Array.isArray(fieldErrors) ? fieldErrors[0] : fieldErrors
		return Private.toNonEmptyString(candidate)
	},
	getFirstValidationMessage: function (errors) {
		const fieldErrorsList = errors && typeof errors === 'object' ? Object.values(errors) : []
		return fieldErrorsList.map(Private.getFirstFieldMessage).find(message => message !== null) ?? null
	},
	getValidationErrors: function (data) {
		let result = null
		if (data?.errors && typeof data.errors === 'object') {
			result = data.errors
		} else if (data?.error && typeof data.error === 'object') {
			result = Object.fromEntries(Object.entries(data.error).filter(([key]) => !LOGIN_ERROR_META_KEYS.includes(key)))
		}
		return result
	},
	getFieldErrors: function (data) {
		const validationErrors = Private.getValidationErrors(data) ?? {}
		return Object.fromEntries(LOGIN_FIELDS.map(field => [field, Private.getFirstFieldMessage(validationErrors[field])]))
	},
	getBackendMessage: function (data) {
		const error = data?.error
		let result = null
		if (typeof error === 'string') {
			result = Private.toNonEmptyString(error)
		} else if (error && typeof error === 'object') {
			result = Private.toNonEmptyString(error.message) ?? Private.getFirstValidationMessage(Private.getValidationErrors({ error }))
		}
		return result ?? Private.getFirstValidationMessage(data?.errors) ?? Private.toNonEmptyString(data?.message)
	},
	toLoginError: function (error) {
		const response = error?.response
		const status = response?.status ?? null
		const data = response?.data
		const result = {
			type: LoginErrorTypes.UNKNOWN,
			status: status,
			code: data?.error?.code ?? data?.code ?? null,
			message: Private.getBackendMessage(data),
			fieldErrors: Private.getFieldErrors(data),
			retryAfterSeconds: null,
			cause: error
		}
		if (status === 401) {
			result.type = LoginErrorTypes.INVALID_CREDENTIALS
		} else if (status === 422) {
			result.type = LoginErrorTypes.INVALID_INPUT
		} else if (status === 429) {
			result.type = LoginErrorTypes.THROTTLED
			result.retryAfterSeconds = Private.getRetryAfterSeconds(response)
		} else if (!status || SERVICE_UNAVAILABLE_STATUSES.includes(status)) {
			result.type = LoginErrorTypes.SERVICE_UNAVAILABLE
		}
		return result
	},
	setAuthStoreData: function (data) {
		return Promise.all([
			STORE.dispatch('auth/setToken', data?.token),
			STORE.dispatch('auth/setIsAuthenticated', data?.auth),
			STORE.dispatch('auth/setIsSuperAdmin', data?.isAdmin),
			STORE.dispatch('auth/setIsAccountant', data?.isAccountant),
			// Bloc `password` renvoyé par login/autologin/retrievelogin : permet de savoir
			// dès la connexion qu'un changement de mot de passe est requis (must_change),
			// sans attendre un premier 403 PASSWORD_EXPIRED sur une requête quelconque.
			STORE.dispatch('passwordPolicy/applyPasswordBlock', data?.password)
		])
	},
	getIsAuthenticated: function () {
		return STORE.state.auth.isAuthenticated
	},
	setToken: function (token) {
		return STORE.dispatch('auth/setToken', token)
	}
}
// -------
// Exports
// -------
export default {
	setToken: Private.setToken,
	autoLogin: function (refreshToken = true, isMobileApp = false) {
		if (Private.getIsAuthenticated()) {
			return Promise.reject('AuthError : Cannot try autoLogin when a user is already logged')
		}

		const token = localStorage.getItem('token')
		let result
		if (token) {
			result = Private.setToken(token)
				.then(() => {
					if (refreshToken) {
						return API.autoLogin({ show_error: false })
					} else {
						let params = {}
						if (isMobileApp) {
							params = { filters: { is_mobile_app: true } }
						}
						return API.retrieveLogin({ show_error: false, params })
					}
				})
				.then(results => {
					return Private.setAuthStoreData(results.data)
				})
		} else {
			result = Promise.reject('AuthError: No token was found')
		}

		result = result.catch(error => {
			return STORE.dispatch('auth/reset').then(() => {
				throw error
			})
		})

		return result
	},
	login: function (email, password) {
		const config = { show_error: false, headers: { 'Accept-Language': i18n.locale } }
		return API.login({ email, password }, config).then(
			res => Private.setAuthStoreData(res?.data),
			error => {
				throw Private.toLoginError(error)
			}
		)
	},
	logout: function () {
		if (STORE.state.modules.list.some(obj => obj.name === IDENTITY_PROVIDER_MODULE_NAME)) {
			return KeycloakService.logout().finally(() => {
				return STORE.dispatch('reset').finally(() => {
					AppService.goTo('signin')
				})
			})
		} else {
			return API.logout()
				.then(() => {
					return STORE.dispatch('reset')
				})
				.finally(() => {
					AppService.goTo('signin')
				})
		}
	},
	getIsAuthenticated: Private.getIsAuthenticated,
	getIsAccountant: function () {
		return STORE.state.auth.isAccountant
	},
	getIsSuperAdmin: function () {
		return STORE.state.auth.isSuperAdmin
	},
	resetAuthentication: function () {
		return STORE.dispatch('auth/reset')
	}
}
